import { useCallback, useEffect, useRef, useState } from 'react';
import { useDeleteWidgetMutation } from '../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../redux/api/utils/isSuccessfulResult';
import { EditorSaveTracker } from '../saves/editorSaveTracker';

/** The Undo toast stays this long; the DELETE runs when it closes (design d10, owner decision 5). */
export const UNDO_DELETE_MS = 8000;

export interface PendingDelete {
  widgetUUID: string;
  /** The toast text: 'Block "Tickets" deleted.' */
  message: string;
}

interface UseDeleteWithUndoProps {
  accountId: number;
  smartLinkId: number;
  /** Told the toast text when a DELETE fails: the row is back then. */
  onFailed: (widgetUUID: string) => void;
  /** The save tracker of the editor: Publish sends a delete that waits for its Undo window first. */
  saves?: EditorSaveTracker | null;
}

const SAVER_KEY = 'delete-with-undo';

/**
 * Delete with Undo, no question first (design d10). The row hides at once and the toast shows for
 * 8 s; the DELETE call runs when the toast closes (time out, close button, a second delete, or the
 * editor closing). Undo puts the row back and sends nothing.
 */
export function useDeleteWithUndo({ accountId, smartLinkId, onFailed, saves = null }: UseDeleteWithUndoProps) {
  const [deleteWidget] = useDeleteWidgetMutation();
  const [pending, setPending] = useState<PendingDelete | null>(null);
  // Rows whose DELETE is on its way: they stay hidden until the answer replaces the page.
  const [inFlight, setInFlight] = useState<ReadonlySet<string>>(() => new Set());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushRef = useRef<() => void>(() => undefined);
  const pendingRef = useRef<PendingDelete | null>(null);
  pendingRef.current = pending;

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    saves?.unregister(SAVER_KEY);
  }, [saves]);

  const send = useCallback(
    (item: PendingDelete) => {
      setInFlight((current) => new Set(current).add(item.widgetUUID));
      const done = (ok: boolean) => {
        setInFlight((current) => {
          const next = new Set(current);
          next.delete(item.widgetUUID);
          return next;
        });
        if (!ok) {
          onFailed(item.widgetUUID);
        }
      };
      const call = deleteWidget({ accountId, smartLinkId, widgetUUID: item.widgetUUID });
      (saves?.track(call, (result) => !isSuccessfulResult(result)) ?? call)
        .then((result) => done(isSuccessfulResult(result)))
        .catch(() => done(false));
    },
    [accountId, deleteWidget, onFailed, saves, smartLinkId]
  );

  /** Closes the toast and sends the DELETE now. */
  const flush = useCallback(() => {
    clearTimer();
    const item = pendingRef.current;
    if (item === null) {
      return;
    }
    pendingRef.current = null;
    setPending(null);
    send(item);
  }, [clearTimer, send]);

  const remove = useCallback(
    (item: PendingDelete) => {
      // One toast at a time: a second delete closes the first one, which sends its DELETE.
      flush();
      pendingRef.current = item;
      setPending(item);
      timerRef.current = setTimeout(flush, UNDO_DELETE_MS);
      // Publish in the Undo window sends the delete first, so the live page does not keep the block.
      saves?.register(SAVER_KEY, () => flushRef.current(), 'undo');
    },
    [flush, saves]
  );

  const undo = useCallback(() => {
    clearTimer();
    pendingRef.current = null;
    setPending(null);
  }, [clearTimer]);

  // The editor closes: the artist asked for the delete, so it runs now.
  flushRef.current = flush;
  useEffect(() => () => flushRef.current(), []);

  const isGone = useCallback(
    (widgetUUID: string) => pending?.widgetUUID === widgetUUID || inFlight.has(widgetUUID),
    [inFlight, pending]
  );

  return { pending, remove, undo, flush, isGone };
}

export type DeleteWithUndo = ReturnType<typeof useDeleteWithUndo>;
